/**
 * Type definitions for Feature Flags
 *
 * Re-exports types from @erebus/api-types (auto-generated from backend Swagger).
 * These types are automatically synced with the backend API.
 */
import type { paths, definitions } from '@erebus/api-types'

// ============================================================================
// API RESPONSE/REQUEST TYPES (from backend Swagger)
// ============================================================================

/**
 * Response from GET /features
 * Returns all feature flags as a simple key-value map
 * @example { "features": { "communication_v2_enabled": true, "new_ui": false } }
 */
export type FeaturesMapResponse = paths['/features']['get']['responses']['200']['schema']

/**
 * Response from GET /features/detailed
 * Returns all feature flags with full details (array)
 */
export type FeatureDetailResponse = definitions['pkg_features.FeatureDetailResponse']

/**
 * Response from GET /features/{key}
 * Returns enabled/disabled state of a specific flag
 * @example { "enabled": true }
 */
export type FeatureFlagState = paths['/features/{key}']['get']['responses']['200']['schema']

/**
 * Request body for PATCH /features/{key}
 * Used to update a feature flag (admin only)
 */
export type UpdateFeatureFlagRequest = definitions['pkg_features.UpdateFeatureHandlerRequest']

/**
 * Error response type (used across all endpoints)
 */
export type ErrorResponse = definitions['middleware.ErrorResponse']

// ============================================================================
// CONVENIENCE TYPES
// ============================================================================

/**
 * Simple map of feature flag keys to their enabled/disabled state
 * Used for in-memory caching
 */
export type FeaturesMap = Record<string, boolean>

/**
 * Domain model for detailed feature flags with Date objects
 * Converts ISO string dates from API to Date objects for easier use in Vue components
 */
export interface FeatureFlagDetail {
  id: number
  key: string
  enabled: boolean
  description: string
  defaultValue: boolean
  rolloutPercentage: number // 0-100, percentage of clients that see this feature
  expiresAt: Date
  createdAt: Date
  updatedAt: Date
  createdBy: number
  updatedBy: number
}

// ============================================================================
// MAPPER FUNCTIONS
// ============================================================================

/**
 * Converts API response (ISO string dates) to domain model (Date objects)
 * Use this when working with detailed feature flags in components
 */
export function mapFeatureFlagDetailFromResponse(response: FeatureDetailResponse): FeatureFlagDetail {
  return {
    id: response.id ?? 0,
    key: response.key ?? '',
    enabled: response.enabled ?? false,
    description: response.description ?? '',
    defaultValue: response.defaultValue ?? false,
    rolloutPercentage: response.rolloutPercentage ?? 100, // Default to 100% for backward compatibility
    expiresAt: new Date(response.expiresAt ?? ''),
    createdAt: new Date(response.createdAt ?? ''),
    updatedAt: new Date(response.updatedAt ?? ''),
    createdBy: response.createdBy ?? 0,
    updatedBy: response.updatedBy ?? 0
  }
}
